import { useContext, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
  Pagination,
  IconButton,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import api from '../../../BrandOnBoarding/api';
import { AuthContext } from '../../../context/auth-context';
import MonitoringReportModal from './MonitoringReportModal';
import MonitoringHTMLReportHistoryItem from './MonitoringHTMLReportHistoryItem';
import HTMLReportViewer from '../../BrandAudit/HTMLReportTab/HTMLReportViewer';
import useMonitoringHTMLReportHistory from './useMonitoringHTMLReportHistory';
import CustomizedTables from '../../../Component/UI/Table/Table';
import classes from './MonitoringHTMLReportTab.module.css';

const PAGE_SIZE = 5;

const TABLE_HEADERS = [
  { id: 'brand', name: 'Brand / Product', sorting: false },
  { id: 'category', name: 'Category', sorting: false },
  { id: 'llm', name: 'LLM Providers', sorting: false },
  { id: 'locations', name: 'Locations', sorting: false },
  { id: 'markets', name: 'Markets', sorting: false },
  { id: 'personas', name: 'Personas', sorting: false },
  { id: 'reportType', name: 'Report Type', sorting: false },
  { id: 'date', name: 'Date', sorting: false },
  { id: 'status', name: 'Status', sorting: false },
  { id: 'actions', name: 'Actions', sorting: false },
];

export default function MonitoringHTMLReportTab({
  brandName,
  brandItems = [],
  categories = [],
  personas = [],
  markets = [],
  geography = [],
  dbLlms = [],
  customerLlm = [],
  availableDates = [],
  onGenerateJson,
  defaults = {},
  selectedOptionShow = 'Company/Brand',
  customerId,
  brandFilters = [],
}) {
  const { user } = useContext(AuthContext);
  const userId = user?.user_role?.user_id;

  const [showModal, setShowModal] = useState(false);
  const [generating, setGenerating] = useState(0);
  const [generatingJson, setGeneratingJson] = useState(false);
  const [page, setPage] = useState(1);

  // Inline viewer state
  const [inlineViewJobId, setInlineViewJobId] = useState(null);
  const [inlineHtml, setInlineHtml] = useState('');
  const [inlineLoading, setInlineLoading] = useState(false);

  // Duplicate confirmation dialog
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    existingJob: null,
    pendingSelections: null,
  });

  const {
    jobs,
    loading: historyLoading,
    generateReport,
    addJob,
    findCompletedJob,
    downloadReport,
    viewReport,
    cancelJob,
    refreshHistory,
  } = useMonitoringHTMLReportHistory({ userId, customerId, selectedOptionShow });

  // ── Pagination ─────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(jobs.length / PAGE_SIZE);
  const pagedJobs = jobs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const handlePageChange = (_, newPage) => setPage(newPage);

  // ── Inline view ────────────────────────────────────────────────────────────

  const handleViewInline = useCallback(async (jobId) => {
    setInlineLoading(true);
    setInlineViewJobId(jobId);
    setInlineHtml('');
    try {
      const response = await api.get(`/llm-reporting/download/${jobId}`, {
        responseType: 'text',
      });
      setInlineHtml(response.data || '');
    } catch {
      toast.error('Failed to load report. Please try again.');
      setInlineViewJobId(null);
    } finally {
      setInlineLoading(false);
    }
  }, []);

  const handleBackToHistory = () => {
    setInlineViewJobId(null);
    setInlineHtml('');
  };

  // ── Viewer save callbacks ──────────────────────────────────────────────────

  const handleSaveUpdate = useCallback(
    async (newHtml) => {
      await api.patch(`/llm-reporting/html-report/${inlineViewJobId}`, {
        html: newHtml,
        userId,
      });
      await refreshHistory();
      setInlineHtml(newHtml);
    },
    [inlineViewJobId, userId, refreshHistory]
  );

  const handleSaveNew = useCallback(
    async (newHtml) => {
      await api.post('/llm-reporting/html-report', {
        html: newHtml,
        userId,
        sourceJobId: inlineViewJobId,
      });
      await refreshHistory();
      setPage(1);
    },
    [inlineViewJobId, userId, refreshHistory]
  );

  // ── Open in new tab ────────────────────────────────────────────────────────

  const handleViewInNewTab = useCallback(
    async (jobId) => {
      try {
        await viewReport(jobId);
      } catch (err) {
        if (err?.message === 'popup_blocked') {
          toast.warn('Popup blocked. Please allow popups for this site.');
        } else {
          toast.error('Failed to open report.');
        }
      }
    },
    [viewReport]
  );

  // ── Generate HTML report flow ──────────────────────────────────────────────

  const initiateReportGeneration = async (selections) => {
    setGenerating((n) => n + 1);
    try {
      const jobId = await generateReport(selections);
      addJob(jobId, selections);
      setPage(1);
      toast.info('Report generation started. Track status in the table below.', {
        autoClose: 5000,
      });
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || 'Failed to start report generation';
      toast.error(msg);
    } finally {
      setGenerating((n) => n - 1);
    }
  };

  const handleGenerate = async (selections) => {
    setShowModal(false);
    const existing = findCompletedJob(selections);
    if (existing) {
      setDuplicateDialog({ open: true, existingJob: existing, pendingSelections: selections });
      return;
    }
    await initiateReportGeneration(selections);
  };

  // ── Generate JSON ──────────────────────────────────────────────────────────

  const handleGenerateJson = async (selections) => {
    setGeneratingJson(true);
    try {
      // setShowModal(false);
      await onGenerateJson(selections);
    } finally {
      setGeneratingJson(false);
    }
  };

  // ── Duplicate dialog ───────────────────────────────────────────────────────

  const handleDuplicateViewExisting = async () => {
    const { existingJob } = duplicateDialog;
    setDuplicateDialog({ open: false, existingJob: null, pendingSelections: null });
    await handleViewInline(existingJob.jobId);
  };

  const handleDuplicateRegenerate = async () => {
    const { pendingSelections } = duplicateDialog;
    setDuplicateDialog({ open: false, existingJob: null, pendingSelections: null });
    await initiateReportGeneration(pendingSelections);
  };

  // ── In-progress badge ──────────────────────────────────────────────────────

  const inProgressCount = jobs.filter(
    (j) => j.status === 'in-progress' || j.status === 'pending'
  ).length;

  // ─────────────────────────────────────────────────────────────────────────
  // INLINE VIEWER MODE
  // ─────────────────────────────────────────────────────────────────────────

  if (inlineViewJobId) {
    if (inlineLoading) {
      return (
        <Box className={classes.root}>
          <Box className={classes.centered} style={{ minHeight: 400 }}>
            <CircularProgress />
          </Box>
        </Box>
      );
    }
    return (
      <HTMLReportViewer
        html={inlineHtml}
        jobId={inlineViewJobId}
        userId={userId}
        onBack={handleBackToHistory}
        onSaveUpdate={handleSaveUpdate}
        onSaveNew={handleSaveNew}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // HISTORY TABLE MODE
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box className={classes.root}>
      {/* ── Header row ── */}
      <Box className={classes.headerRow}>
        <Box className={classes.titleArea}>
          <Typography className={classes.title}>Reporting</Typography>
          {inProgressCount > 0 && (
            <Box className={classes.inProgressBadge}>
              <CircularProgress size={10} thickness={5} />
              <span>{inProgressCount} generating…</span>
            </Box>
          )}
        </Box>

        <Box className={classes.actions}>
          <Tooltip title="Generate new report">
            <span>
              <IconButton
                size="small"
                onClick={() => setShowModal(true)}
                className={classes.actionIcon}
              >
                {generating > 0 ? (
                  <CircularProgress size={18} />
                ) : (
                  <AddCircleOutlineIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={() => { refreshHistory(); setPage(1); }}
                disabled={historyLoading}
                className={classes.actionIcon}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Table ── */}
      <Box className={classes.tableWrapper}>
        {historyLoading && jobs.length === 0 ? (
          <Box className={classes.centered}>
            <CircularProgress />
          </Box>
        ) : jobs.length === 0 ? (
          <Box className={classes.emptyHistory}>
            <Typography className={classes.emptyText}>
              No reports generated yet. Click the <strong>+</strong> button to get started.
            </Typography>
          </Box>
        ) : (
          <CustomizedTables
            headers={TABLE_HEADERS}
            data={pagedJobs}
            elevation={0}
            size="medium"
            render={(item, index) => (
              <MonitoringHTMLReportHistoryItem
                key={item.jobId}
                item={item}
                index={index + (page - 1) * PAGE_SIZE}
                onDownload={downloadReport}
                onViewInline={handleViewInline}
                onViewInNewTab={handleViewInNewTab}
                onCancel={cancelJob}
                selectedOptionShow={selectedOptionShow}
              />
            )}
          />
        )}
      </Box>

      {/* ── Pagination ── */}
      {jobs.length > 0 && (
        <Box className={classes.paginationContainer}>
          <Typography className={classes.resultsText}>
            Showing{' '}
            <strong>{Math.min((page - 1) * PAGE_SIZE + 1, jobs.length)}</strong>{' '}
            to <strong>{Math.min(page * PAGE_SIZE, jobs.length)}</strong> of{' '}
            <strong>{jobs.length}</strong> results
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              className={classes.pagination}
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
            />
          )}
        </Box>
      )}

      {/* ── Generate modal ── */}
      <MonitoringReportModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onGenerate={handleGenerate}
        onGenerateJson={handleGenerateJson}
        loading={generating > 0}
        jsonLoading={generatingJson}
        brandName={brandName}
        brandItems={brandItems}
        categories={categories}
        personas={personas}
        markets={markets}
        geography={geography}
        dbLlms={dbLlms}
        customerLlm={customerLlm}
        availableDates={availableDates}
        defaults={defaults}
        brandFilters={brandFilters}
      />

      {/* ── Duplicate dialog ── */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() =>
          setDuplicateDialog({ open: false, existingJob: null, pendingSelections: null })
        }
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Report Already Exists</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A completed HTML report already exists for these parameters
            (Category:{' '}
            <strong>{duplicateDialog.pendingSelections?.category}</strong>).
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Would you like to view the existing report, or generate a new one?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() =>
              setDuplicateDialog({ open: false, existingJob: null, pendingSelections: null })
            }
          >
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleDuplicateViewExisting}>
            View Existing
          </Button>
          <Button variant="contained" onClick={handleDuplicateRegenerate}>
            Regenerate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
